import axios from 'axios'
import {
  FALLBACK_IMAGE,
  BASE_URL,
  buildCategoryDescription,
  buildBreadcrumbJsonLd
} from '~/utils/seo/jsonLd'
/**
 * SSR 인메모리 카테고리 캐시 — /ds/ctgr 조회 결과를 서버 프로세스에 캐싱.
 * TTL 10분, in-flight dedup (thundering-herd 방지), stale-on-error.
 * Bounded: 키(= exhCtgrNos)당 1개 배열.
 */
const CTGR_CACHE_TTL_MS = 10 * 60 * 1000
const CTGR_CACHE_MAX = 200
const _cache = new Map()
const _inflight = new Map()

function fetchCtgr (apiBase, exhCtgrNos) {
  const now = Date.now()
  const key = exhCtgrNos.join(',')
  const hit = _cache.get(key)
  if (hit && (now - hit.ts) < CTGR_CACHE_TTL_MS) {
    return Promise.resolve(hit.data)
  }
  // Stale entry 정리 (TTL 만료 = 메모리 누수 방지)
  if (hit) { _cache.delete(key) }

  // Thundering-herd dedup: 동시 접속해도 동일 키는 1건만 API 호출
  let p = _inflight.get(key)
  if (!p) {
    p = axios.post(apiBase + '/ds/ctgr', { exhCtgrNos })
      .then((res) => {
        const data = (res && res.data && res.data.data) || null
        if (data) {
          // 사이즈 캡: Map 초과 시 가장 오래된 entry 제거
          if (_cache.size >= CTGR_CACHE_MAX) {
            const oldestKey = _cache.keys().next().value
            _cache.delete(oldestKey)
          }
          _cache.set(key, { data, ts: Date.now() })
        }
        return data
      })
      .catch(() => {
        // API 실패 시 기존 캐시 반환 (stale-on-error)
        const stale = _cache.get(key)
        return stale ? stale.data : null
      })
      .finally(() => { _inflight.delete(key) })
    _inflight.set(key, p)
  }
  return p
}

/**
 * 카테고리 페이지 seoMeta 핸들러 (FO)
 * - 경로: /ds/exhCtgr/:cnrId/:lgCtgrNo/:mdCtgrNo?/:smCtgrNo?
 * - 카테고리명 조회 (캐시 적용 — 서버 인메모리 TTL 10분, in-flight dedup, /ds/ctgr)
 */
export default async function handleCategory ({ store, route }) {
  const { cnrId, lgCtgrNo, mdCtgrNo, smCtgrNo } = route.params
  if (!lgCtgrNo) { return }

  const canonicalUrl = `${BASE_URL}${route.path}`

  try {
    // 카테고리명 조회 (대카테고리 목록에서 해당 카테고리 찾기)
    let lgCtgrNm = ''
    let mdCtgrNm = ''
    let smCtgrNm = ''

    // 대카테고리명 조회 (캐시 적용 — TTL 10분)
    const lgCtgrList = await fetchCtgr(process.env.API_BASE_URL, ['CTGR_00001'])
    if (lgCtgrList) {
      const lgCtgr = lgCtgrList.find(item => item.exhCtgrNo === lgCtgrNo)
      if (lgCtgr) { lgCtgrNm = lgCtgr.exhCtgrNm }
    }

    // 중카테고리명 조회 (필요 시, 캐시 적용)
    if (mdCtgrNo) {
      const mdCtgrList = await fetchCtgr(process.env.API_BASE_URL, [lgCtgrNo])
      if (mdCtgrList) {
        const mdCtgr = mdCtgrList.find(item => item.exhCtgrNo === mdCtgrNo)
        if (mdCtgr) { mdCtgrNm = mdCtgr.exhCtgrNm }
      }
    }

    // 소카테고리명 조회 (필요 시, 캐시 적용)
    if (smCtgrNo && mdCtgrNo) {
      const smCtgrList = await fetchCtgr(process.env.API_BASE_URL, [mdCtgrNo])
      if (smCtgrList) {
        const smCtgr = smCtgrList.find(item => item.exhCtgrNo === smCtgrNo)
        if (smCtgr) { smCtgrNm = smCtgr.exhCtgrNm }
      }
    }

    // 가장 하위 카테고리명 사용
    const categoryName = smCtgrNm || mdCtgrNm || lgCtgrNm || ''
    const categoryPath = [lgCtgrNm, mdCtgrNm, smCtgrNm].filter(Boolean).join(' > ')

    if (!categoryName) {
      // 카테고리명 조회 실패 시 기본값
      store.commit('seoMeta/set', {
        title: '카테고리 - 다이소몰',
        description: '다이소몰에서 다양한 카테고리의 상품을 합리적인 가격에 만나보세요.',
        ogUrl: canonicalUrl,
        ogImage: FALLBACK_IMAGE,
        canonical: canonicalUrl
      })
      return
    }

    const title = `${categoryName} - 다이소몰`
    const description = buildCategoryDescription({ categoryName, categoryPath })

    // BreadcrumbList JSON-LD
    const breadcrumbItems = [{ name: '홈', url: BASE_URL }]
    if (lgCtgrNm) { breadcrumbItems.push({ name: lgCtgrNm, url: `${BASE_URL}/ds/exhCtgr/${cnrId}/${lgCtgrNo}` }) }
    if (mdCtgrNm) { breadcrumbItems.push({ name: mdCtgrNm, url: `${BASE_URL}/ds/exhCtgr/${cnrId}/${lgCtgrNo}/${mdCtgrNo}` }) }
    if (smCtgrNm) { breadcrumbItems.push({ name: smCtgrNm, url: `${BASE_URL}/ds/exhCtgr/${cnrId}/${lgCtgrNo}/${mdCtgrNo}/${smCtgrNo}` }) }
    const breadcrumb = buildBreadcrumbJsonLd(breadcrumbItems)

    store.commit('seoMeta/set', {
      title,
      description,
      ogUrl: canonicalUrl,
      ogImage: FALLBACK_IMAGE,
      ogType: 'website',
      canonical: canonicalUrl,
      mobileUrl: `https://m.daisomall.co.kr/main/ds/exhCtgr/${cnrId}/${lgCtgrNo}${mdCtgrNo ? '/' + mdCtgrNo : ''}${smCtgrNo ? '/' + smCtgrNo : ''}`,
      h1: categoryName,
      jsonLd: [breadcrumb]
    })
  } catch (e) {
    console.error('[seoMeta/category]', e.message)
    store.commit('seoMeta/set', {
      title: '카테고리 - 다이소몰',
      description: '다이소몰에서 다양한 카테고리의 상품을 합리적인 가격에 만나보세요.',
      ogUrl: canonicalUrl,
      ogImage: FALLBACK_IMAGE,
      canonical: canonicalUrl
    })
  }
}
